import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { CompositeModel } from '../complex/composite.model';
import { TextModel } from '../primitive/text.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { isDefined } from '../../utilities/is-defined';
import { CrmCodeModel } from '../complex/crm-code.model';
import { NumberModel } from '../primitive/number.model';
import moment from 'moment/moment';

export class RadioActiveModel extends CompositeModel {
    public location: TextModel;
    public meterOrCpm: NumberModel;
    public net: NumberModel;
    public dpm: NumberModel;
    public isEditable = false;
    public isDeletable = false;

    public constructor(oDataLocation = '') {
        super('RadioActiveModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        // Manually added entry can be deleted and edited
        this.isDeletable = !oDataLocation;
        this.isEditable = !oDataLocation;

        this.location = <TextModel>this.set(new TextModel('Location', oDataLocation));
        modelOptionsLoader.load(this.location, 'RadioActive', 'Location');

        this.location.options = {
            ...this.location.options,
            readonly: !!oDataLocation,
        };

        this.meterOrCpm = <NumberModel>this.set(new NumberModel('meterOrCpm', undefined));
        modelOptionsLoader.load(this.meterOrCpm, 'RadioActive', 'meterOrCpm');

        this.net = <NumberModel>this.set(new NumberModel('net', undefined));
        modelOptionsLoader.load(this.net, 'RadioActive', 'net');

        this.dpm = <NumberModel>this.set(new NumberModel('dpm', undefined));
        modelOptionsLoader.load(this.dpm, 'RadioActive', 'dpm');
        this.calculateNetAndDpm();
    }

    public static fromOData(location: string): RadioActiveModel {
        return new RadioActiveModel(location);
    }

    public getRadioActiveSurveyCrmCodeModel(): CrmCodeModel {
        const codeText = `${this.location.value};${this.meterOrCpm.value};${this.net.value};${this.dpm.value}`;

        return new CrmCodeModel({
            codeType: 'A',
            codeGroup: 'SURV',
            code: 'RSR',
            codeText: codeText,
            startDateTime: moment().startOf('minute'),
            endDateTime: moment().startOf('minute'),
        });
    }

    /**
     * Net = Meter or CPM minus the background reading (BCG), never below 0; DPM = Net / calibration value.
     * Without a calibration value only the field options are refreshed, so restored values are kept.
     *
     * @param background the background reading (BCG) shared by all survey rows
     * @param calibrationValue the configured radioactive survey tool calibration
     */
    public calculateNetAndDpm(background?: number, calibrationValue?: number): void {
        if (isDefined(calibrationValue)) {
            this.net.value =
                isDefined(this.meterOrCpm.value) && isDefined(background)
                    ? Math.max(0, this.meterOrCpm.value - background)
                    : undefined;
            // Use .toFixed(2) to limit the result to two decimal place (e.g., 12.6666666 → 12.67)
            // If Net is empty, dpm would become 0 and the field wrongly turns valid.
            // Set dpm undefined to keep it empty and invalid.
            this.dpm.value = isDefined(this.net.value)
                ? Number((this.net.value / calibrationValue).toFixed(2))
                : undefined;
        }
        this.net = assignModelOptions(
            this.net,
            {
                ...this.net.options,
                allowReadonlyValidation: true,
            },
            this,
        );
        this.dpm = assignModelOptions(
            this.dpm,
            {
                ...this.dpm.options,
                allowReadonlyValidation: true,
                required: !this.dpm.value,
            },
            this,
        );
    }

    public toJSON(): JsonRadioActiveModel {
        return {
            location: this.location.value,
            meterOrCpm: this.meterOrCpm.value,
            net: this.net.value,
            dpm: this.dpm.value,
            isEditable: this.isEditable,
            isDeletable: this.isDeletable,
        };
    }
}

export class RadioActiveModelFromJson extends RadioActiveModel {
    public constructor(data: JsonRadioActiveModel) {
        super();
        this.location.value = data.location;
        this.net.value = data.net;
        this.meterOrCpm.value = data.meterOrCpm;
        this.isDeletable = data.isDeletable;
        this.isEditable = data.isEditable;
        this.dpm.value = data.dpm;
        this.location.options = {
            ...this.location.options,
            readonly: !data.isEditable,
        };
        this.calculateNetAndDpm();
    }
}

export interface JsonRadioActiveModel {
    location: string;
    meterOrCpm: number | undefined;
    net: number | undefined;
    dpm: number | undefined;
    isEditable: boolean;
    isDeletable: boolean;
}
